import dayjs from 'dayjs';
import * as Yup from 'yup';

// date: 2024-01-01
export const unformatDate = (date: string) => {
  const dateSplitted = date.split('-').reverse();
  const dateFormatted = dateSplitted.join('/');

  return dateFormatted;
};

const getBirthDate = (value: string) => {
  const [day, month, year] = value.split('/');
  return dayjs(`${year}-${month}-${day}`);
};

/**
 * @param birthDate - The birth date in the format DD/MM/YYYY
 * @returns true if the birth date is higher or equal than 18 years, false otherwise
 */
export const isBirthDateHigherOrEqualThan18Years = (birthDate: string) => {
  return isAtLeastAge(getBirthDate(birthDate), 18);
};

export function getFormatDate(date: string) {
  const formattedDate = date.split('/').reverse().join('-');
  return formattedDate;
}

const isAtLeastAge = (birthDate: dayjs.Dayjs, age: number) =>
  !birthDate.isAfter(dayjs().subtract(age, 'year'), 'day');

const isAtMostAge = (birthDate: dayjs.Dayjs, age: number) =>
  !birthDate.isBefore(dayjs().subtract(age, 'year'), 'day');

const teenAgeRules = {
  teens: {
    minAge: 11,
    maxAge: 16,
    underAgeError: 'teensAgeUnder11',
    overAgeError: 'teensAgeOver16',
    underAgeMessage:
      'Aluno menor que 11 anos. O curso Teens é voltado para alunos a partir de 11 anos. Solicite ao comercial o curso correspondente à faixa etária informada',
    overAgeMessage:
      'Aluno maior que 16 anos. O curso Teens é voltado para alunos até 16 anos. Solicite ao comercial o curso correspondente à faixa etária informada.',
  },
  'conquer-teens-formacao': {
    minAge: 15,
    maxAge: 18,
    underAgeError: 'conquerTeensAgeUnder15',
    overAgeError: 'conquerTeensAgeOver18',
    underAgeMessage:
      'Aluno menor que 15 anos. O curso Conquer Teens é voltado para alunos a partir de 15 anos. Solicite ao comercial o curso correspondente à faixa etária informada.',
    overAgeMessage:
      'Aluno maior que 18 anos. O curso Conquer Teens é voltado para alunos até 18 anos. Solicite ao comercial o curso correspondente à faixa etária informada.',
  },
  silver: {
    minAge: 60,
    underAgeError: 'wusSilverAgeUnder60',
    underAgeMessage:
      'Aluno menor que 60 anos. O curso Wise Up Silver é voltado para alunos a partir de 60 anos. Solicite ao comercial o curso correspondente à faixa etária informada.',
  },
  n1: {
    minAge: 17,
    underAgeError: 'numberOneAgeUnder17',
    underAgeMessage:
      'Aluno menor que 17 anos. O curso Number One é voltado para alunos a partir de 17 anos. Solicite ao comercial o curso correspondente à faixa etária informada.',
  },
} as const;

const getTeenAgeRule = (productCode?: string) =>
  teenAgeRules[productCode?.toLowerCase() as keyof typeof teenAgeRules];

export const birthDateValidation = (
  productCode?: string,
  getError?: (key: string) => string
) => {
  const teenAgeRule = getTeenAgeRule(productCode);

  return Yup.string()
    .matches(
      /^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[0-2])\/\d{4}$/,
      getError
        ? getError('invalidBirthDateFormat')
        : 'Insira uma data válida no formato DD/MM/YYYY.'
    )
    .required(
      getError
        ? getError('invalidBirthDateRequired')
        : 'Insira uma data válida.'
    )
    .test(
      'birthDateTooOld',
      getError
        ? getError('invalidBirthDateTooOld')
        : 'A data de nascimento não pode ser anterior a 01/01/1900.',
      (value) => {
        if (!value || typeof value !== 'string') {
          return true;
        }
        const birthDate = getBirthDate(value);
        const minimumDate = dayjs('1900-01-01');
        return birthDate.isAfter(minimumDate) || birthDate.isSame(minimumDate);
      }
    )
    .test(
      'teensAgeOverMax',
      teenAgeRule && 'overAgeError' in teenAgeRule && getError
        ? getError(teenAgeRule.overAgeError)
        : 'overAgeMessage' in (teenAgeRule ?? {})
          ? (teenAgeRule as { overAgeMessage: string }).overAgeMessage
          : '',
      (value) => {
        if (
          !teenAgeRule ||
          !('maxAge' in teenAgeRule) ||
          !value ||
          typeof value !== 'string'
        ) {
          return true;
        }
        return isAtMostAge(getBirthDate(value), teenAgeRule.maxAge);
      }
    )
    .test(
      'teensAgeUnderMin',
      teenAgeRule && getError
        ? getError(teenAgeRule.underAgeError)
        : (teenAgeRule?.underAgeMessage ?? ''),
      (value) => {
        if (!teenAgeRule || !value || typeof value !== 'string') {
          return true;
        }
        return isAtLeastAge(getBirthDate(value), teenAgeRule.minAge);
      }
    )
    .test(
      'execAgeUnder16',
      'Aluno menor que 16 anos. O curso Executive é voltado para alunos a partir de 16 anos. Solicite ao comercial o curso correspondente à faixa etária informada.',
      (value) => {
        if (
          productCode?.toLowerCase() !== 'exec' ||
          !value ||
          typeof value !== 'string'
        ) {
          return true;
        }
        return isAtLeastAge(getBirthDate(value), 16);
      }
    );
};
